/**
 * Partage d'une retouche vers Discord.
 *
 * LE CLIENT NE CONNAIT AUCUNE URL DE WEBHOOK, et c'est toute la raison de ce
 * detour : il envoie le GIF au serveur de jeu, qui relaie. Une URL de webhook
 * posee dans le bundle serait lisible par n'importe qui et suffirait a poster
 * sous l'identite du serveur.
 *
 * POURQUOI HTTP ET PAS LA SOCKET : la socket de jeu est plafonnee a 32 Ko par
 * trame, un GIF pese mille fois plus. Relever ce plafond pour un partage
 * occasionnel ouvrirait a chaque connexion le droit d'envoyer plusieurs
 * mega-octets, dix fois par seconde, avant meme d'avoir ete identifiee.
 *
 * Le GIF est celui que la galerie sait deja produire : rien n'est encode deux
 * fois, et le fichier partage est exactement celui qu'on telecharge.
 */
import { MAX_SHARE_GIF_BYTES } from '@retusch/shared';
import { serverHttpBase, sessionTokenStore } from '../net/connection.js';

/** Ce qui peut echouer, et ce que le joueur doit en comprendre. */
export type ShareOutcome = 'ok' | 'too-large' | 'busy' | 'limit' | 'failed';

/**
 * Envoie le GIF. Rend un verdict, jamais une exception : un partage rate n'est
 * pas une panne du jeu, et rien dans la partie n'en depend.
 */
export async function shareGifToDiscord(
  gif: Blob,
  roomCode: string,
  slot: number,
): Promise<ShareOutcome> {
  // Verifie AVANT d'envoyer : faire monter sept mega-octets pour se les faire
  // refuser gaspille la connexion du joueur, souvent la plus lente des deux.
  // Le serveur revalide de son cote, il ne fait jamais confiance a ce test.
  if (gif.size > MAX_SHARE_GIF_BYTES) return 'too-large';

  const token = sessionTokenStore().get();
  if (token === undefined) return 'failed';

  try {
    const response = await fetch(
      `${serverHttpBase()}/share?room=${encodeURIComponent(roomCode)}&item=${slot}`,
      {
        method: 'POST',
        headers: { 'content-type': 'image/gif', 'x-session-token': token },
        body: gif,
      },
    );
    if (response.ok) return 'ok';

    const body = (await response.json().catch(() => null)) as { reason?: unknown } | null;
    const reason = typeof body?.reason === 'string' ? body.reason : '';
    if (reason === 'too-large') return 'too-large';
    if (reason === 'busy') return 'busy';
    if (reason === 'limit') return 'limit';
    return 'failed';
  } catch {
    return 'failed';
  }
}
